<script setup>
import { ref, computed } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import {
  DuplicateClientException,
  ExceptionWithMessage,
} from 'shared/helpers/CustomErrors';
import ClientsCard from 'dashboard/components-next/Clients/ClientsCard/ClientsCard.vue';

defineProps({ clients: { type: Array, required: true } });

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const route = useRoute();

const uiFlags = useMapGetter('clients/getUIFlags');
const isUpdating = computed(() => uiFlags.value.isUpdating);
const expandedCardId = ref(null);

const updateClient = async updatedData => {
  try {
    await store.dispatch('clients/update', updatedData);
    useAlert(t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.SUCCESS_MESSAGE'));
  } catch (error) {
    const i18nPrefix = 'CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM';
    if (error instanceof DuplicateClientException) {
      if (error.data.includes('email')) {
        useAlert(t(`${i18nPrefix}.EMAIL_ADDRESS.DUPLICATE`));
      } else if (error.data.includes('phone_number')) {
        useAlert(t(`${i18nPrefix}.PHONE_NUMBER.DUPLICATE`));
      }
    } else if (error instanceof ExceptionWithMessage) {
      useAlert(error.data);
    } else {
      useAlert(t(`${i18nPrefix}.ERROR_MESSAGE`));
    }
  }
};

const onClickViewDetails = async id => {
  const routeTypes = {
    clients_dashboard_segments_index: ['clients_edit_segment', 'segmentId'],
    clients_dashboard_labels_index: ['clients_edit_label', 'label'],
  };
  const [name, paramKey] = routeTypes[route.name] || ['clients_edit'];
  const params = {
    clientId: id,
    ...(paramKey && { [paramKey]: route.params[paramKey] }),
  };

  await router.push({ name, params, query: route.query });
};

const toggleExpanded = id => {
  expandedCardId.value = expandedCardId.value === id ? null : id;
};
</script>

<template>
  <div class="flex flex-col gap-4 px-6 pt-4 pb-6">
    <ClientsCard
      v-for="client in clients"
      :id="client.id"
      :key="client.id"
      :name="client.name"
      :email="client.email"
      :thumbnail="client.thumbnail"
      :phone-number="client.phoneNumber"
      :additional-attributes="client.additionalAttributes"
      :availability-status="client.availabilityStatus"
      :is-expanded="expandedCardId === client.id"
      :is-updating="isUpdating"
      @toggle="toggleExpanded(client.id)"
      @update-client="updateClient"
      @show-client="onClickViewDetails"
    />
  </div>
</template>
